/**
 * Charting Templates — list + builder with overlay authoring (PRD.171, PRD.172).
 *
 * Org-level reusable charting template records.
 * Each template stores a base image, metadata, and structured overlay definitions.
 * Separate from encounter-time Draw workflows.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Stage, Layer, Rect, Text, Transformer, Image as KonvaImage } from 'react-konva';
import Konva from 'konva';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  useBrandTokens,
  PageHeader,
  SidebarToggleButton,
  type ShellOutletContext,
} from '../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { qChartingTemplates, type ChartingTemplateRow } from '@helios/data-access';
import { uploadToStorageScoped } from '../../lib/upload-to-storage';

// ─── Overlay types (PRD.172 §7) ───

type OverlayType = 'text' | 'field';

interface OverlayBase {
  id: string;
  type: OverlayType;
  x: number;
  y: number;
}

interface TextOverlay extends OverlayBase {
  type: 'text';
  text: string;
  fontSize: number;
}

/** Binding keys for field overlays — maps to patient/visit/charting data. */
type FieldBindingKey =
  | 'patient_name'
  | 'patient_dob'
  | 'patient_age'
  | 'patient_gender'
  | 'visit_date'
  | 'visit_number'
  | 'provider_name'
  | 'diagnosis'
  | 'notes'
  | 'custom';

const FIELD_BINDING_OPTIONS: { key: FieldBindingKey; label: string }[] = [
  { key: 'patient_name', label: 'Patient Name' },
  { key: 'patient_dob', label: 'Date of Birth' },
  { key: 'patient_age', label: 'Age' },
  { key: 'patient_gender', label: 'Gender' },
  { key: 'visit_date', label: 'Visit Date' },
  { key: 'visit_number', label: 'Visit #' },
  { key: 'provider_name', label: 'Provider' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'notes', label: 'Notes' },
  { key: 'custom', label: 'Custom Label' },
];

interface FieldOverlay extends OverlayBase {
  type: 'field';
  bindingKey: FieldBindingKey;
  label: string; // display label (auto-derived from binding, or custom)
  width: number;
  height: number;
}

type OverlayItem = TextOverlay | FieldOverlay;

interface TemplateConfig {
  overlays: OverlayItem[];
}

type ToolMode = 'select' | 'text';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function parseConfig(raw: string | null): TemplateConfig {
  if (!raw) return { overlays: [] };
  try {
    const parsed = JSON.parse(raw);
    return { overlays: Array.isArray(parsed.overlays) ? parsed.overlays : [] };
  } catch {
    return { overlays: [] };
  }
}

// ─── Main Component ───

export function ChartingTemplatesPage() {
  const brandTokens = useBrandTokens();
  const primary = brandTokens.primary;
  const { claims } = useAuth();
  const { collapsed, onToggleSidebar } = useOutletContext<ShellOutletContext>();
  const orgId = claims?.activeOrg ?? '';
  const { db } = usePowerSyncDb();

  const { data: templates = [] } = usePowerSyncQuery<ChartingTemplateRow>(
    qChartingTemplates(orgId),
  );

  const [editing, setEditing] = useState<ChartingTemplateRow | 'new' | null>(null);

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        orgId={orgId}
        db={db}
        collapsed={collapsed}
        onToggleSidebar={onToggleSidebar}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Charting Templates"
        subtitle="Reusable visual templates for charting workflows"
        collapsed={collapsed}
        onToggle={onToggleSidebar}
      >
        <button
          onClick={() => setEditing('new')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <NavIcon name="plus" />
          New Template
        </button>
      </PageHeader>

      <div style={{ padding: '0 var(--sp-6)' }}>
        {templates.length === 0 ? (
          <EmptyState onCreateNew={() => setEditing('new')} />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 16,
            }}
          >
            {templates.map((tpl) => (
              <TemplateCard key={tpl.id} template={tpl} onClick={() => setEditing(tpl)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Empty state ───

function EmptyState({ onCreateNew }: { onCreateNew: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 'var(--sp-10) var(--sp-5)',
        color: 'var(--text-muted)',
      }}
    >
      <NavIcon name="file-text" />
      <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 500 }}>
        No charting templates yet
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)' }}>
        Create a template to use as a visual base for charting.
      </div>
      <button
        onClick={onCreateNew}
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          color: 'var(--text)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <NavIcon name="plus" />
        Create Template
      </button>
    </div>
  );
}

// ─── Template card ───

function TemplateCard({
  template,
  onClick,
}: {
  template: ChartingTemplateRow;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        overflow: 'hidden',
        cursor: 'pointer',
        padding: 0,
        textAlign: 'left',
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          background: 'var(--bg-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {template.image_url ? (
          <img
            src={template.image_url}
            alt={template.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <NavIcon name="camera" />
        )}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {template.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {template.category && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                textTransform: 'capitalize',
              }}
            >
              {template.category}
            </span>
          )}
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {formatDate(template.updated_at)}
          </span>
        </div>
      </div>
    </button>
  );
}

// ─── Overlay toolbar button ───

function ToolBtn({
  children,
  active,
  onClick,
  title,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '5px 10px',
        borderRadius: 'var(--radius-sm)',
        border: active ? '1.5px solid var(--color-primary)' : '1px solid var(--border)',
        background: active ? 'rgba(59,130,246,0.08)' : 'var(--bg-surface)',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ─── Template editor with overlay authoring ───

function TemplateEditor({
  template,
  orgId,
  db,
  collapsed,
  onToggleSidebar,
  onClose,
}: {
  template: ChartingTemplateRow | null;
  orgId: string;
  db: ReturnType<typeof usePowerSyncDb>['db'];
  collapsed: boolean;
  onToggleSidebar?: () => void;
  onClose: () => void;
}) {
  const isNew = !template;
  const [name, setName] = useState(template?.name ?? '');
  const [category, setCategory] = useState(template?.category ?? '');
  const [imageUrl, setImageUrl] = useState(template?.image_url ?? '');
  const [imageFileId, setImageFileId] = useState(template?.image_file_id ?? '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ─── Overlay state ───
  const [overlays, setOverlays] = useState<OverlayItem[]>(
    () => parseConfig(template?.config ?? null).overlays,
  );
  const [toolMode, setToolMode] = useState<ToolMode>('select');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);

  // ─── Modal state for overlay editing ───
  type ModalState =
    | { kind: 'edit-text'; overlayId: string; value: string }
    | { kind: 'edit-field'; overlayId: string; bindingKey: FieldBindingKey; customLabel: string }
    | { kind: 'new-field'; bindingKey: FieldBindingKey; customLabel: string }
    | null;
  const [modal, setModal] = useState<ModalState>(null);

  // ─── Canvas sizing ───
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const canvasHeight = loadedImage
    ? Math.round(canvasWidth * (loadedImage.naturalHeight / loadedImage.naturalWidth))
    : Math.round(canvasWidth * 0.75);

  useEffect(() => {
    const el = canvasContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setCanvasWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load template image
  useEffect(() => {
    if (!imageUrl) {
      setLoadedImage(null);
      return;
    }
    const img = new window.Image();
    if (!imageUrl.includes('firebasestorage.googleapis.com')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => setLoadedImage(img);
    img.src = imageUrl;
  }, [imageUrl]);

  // Attach transformer to selected node
  useEffect(() => {
    const tr = transformerRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    if (selectedId) {
      const node = stage.findOne('#' + selectedId);
      if (node) {
        tr.nodes([node]);
        tr.getLayer()?.batchDraw();
        return;
      }
    }
    tr.nodes([]);
    tr.getLayer()?.batchDraw();
  }, [selectedId, overlays]);

  // ─── Overlay actions ───
  const addTextOverlay = useCallback(() => {
    const id = crypto.randomUUID().slice(0, 8);
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;
    setOverlays((prev) => [
      ...prev,
      { id, type: 'text' as const, x: cx - 40, y: cy - 10, text: 'Label', fontSize: 16 },
    ]);
    setSelectedId(id);
    setToolMode('select');
  }, [canvasWidth, canvasHeight]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setOverlays((prev) => prev.filter((o) => o.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  const updateOverlay = useCallback((id: string, changes: Partial<OverlayItem>) => {
    setOverlays((prev) =>
      prev.map((o) => (o.id === id ? ({ ...o, ...changes } as OverlayItem) : o)),
    );
  }, []);

  // ─── Stage click: place or deselect ───
  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (toolMode === 'text') {
        addTextOverlay();
        return;
      }
      // Click on empty area → deselect
      if (e.target === e.target.getStage()) {
        setSelectedId(null);
      }
    },
    [toolMode, addTextOverlay],
  );

  // ─── Upload ───
  const handleUpload = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fileId = crypto.randomUUID();
      const result = await uploadToStorageScoped({
        orgId,
        scope: 'templates',
        scopeId: orgId,
        file,
        fileId,
        folder: 'charting_templates',
      });
      setImageUrl(result.downloadUrl);
      setImageFileId(fileId);
    } catch (err) {
      console.error('[ChartingTemplates] upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  // ─── Save ───
  const handleSave = async () => {
    if (!db || !name.trim() || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const configJson = JSON.stringify({ overlays } satisfies TemplateConfig);
      if (isNew) {
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO charting_templates
            (id, org_id, clinic_id, name, description, image_url, image_file_id, category, config, status, created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            name.trim(),
            null,
            imageUrl || null,
            imageFileId || null,
            category.trim() || null,
            configJson,
            'active',
            null,
            now,
            now,
          ],
        );
      } else {
        await db.execute(
          `UPDATE charting_templates
             SET name = ?, description = ?, image_url = ?, image_file_id = ?, category = ?, config = ?, updated_at = ?
           WHERE id = ?`,
          [
            name.trim(),
            null,
            imageUrl || null,
            imageFileId || null,
            category.trim() || null,
            configJson,
            now,
            template.id,
          ],
        );
      }
      onClose();
    } catch (err) {
      console.error('[ChartingTemplates] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!db || !template) return;
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await db.execute(
        `UPDATE charting_templates SET status = 'archived', updated_at = ? WHERE id = ?`,
        [new Date().toISOString(), template.id],
      );
      onClose();
    } catch (err) {
      console.error('[ChartingTemplates] delete failed:', err);
    }
  };

  // ─── Render ───
  return (
    <div>
      {/* Header: editable name + actions */}
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
        }}
      >
        {onToggleSidebar && (
          <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
        )}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isNew ? 'Template name...' : 'Untitled'}
          style={{
            flex: 1,
            fontSize: 'var(--font-size-xl)',
            fontWeight: 600,
            color: 'var(--text)',
            border: '1px solid transparent',
            borderBottom: '1px dashed var(--border)',
            background: 'transparent',
            outline: 'none',
            padding: '2px 6px',
            lineHeight: 'var(--line-height-xl)',
            borderRadius: 'var(--radius-sm)',
            transition: 'border-color 150ms, background 150ms',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-primary)';
            e.currentTarget.style.background = 'var(--bg-surface-2)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.borderBottom = '1px dashed var(--border)';
            e.currentTarget.style.background = 'transparent';
          }}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <option value="">General</option>
          <option value="dental">Dental</option>
          <option value="aesthetic">Aesthetic</option>
        </select>
        {!isNew && (
          <button onClick={handleDelete} style={dangerBtnStyle}>
            <NavIcon name="trash" /> Delete
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={!name.trim() || saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${!name.trim() || saving ? 'var(--border)' : 'var(--color-primary)'}`,
            background: !name.trim() || saving ? 'var(--bg-surface)' : 'var(--color-primary)',
            color: !name.trim() || saving ? 'var(--text-muted)' : '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            cursor: !name.trim() || saving ? 'default' : 'pointer',
            opacity: !name.trim() ? 0.5 : 1,
          }}
        >
          <NavIcon name="check" /> {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} style={mutedBtnStyle}>
          <NavIcon name="chevron-left" /> Back
        </button>
      </div>

      {/* Overlay toolbar (PRD.172 §6) */}
      <div style={{ padding: '0 var(--sp-6)', marginBottom: 'var(--sp-3)' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToolBtn
            active={toolMode === 'select'}
            onClick={() => setToolMode('select')}
            title="Select / move overlays"
          >
            <NavIcon name="cursor" /> Select
          </ToolBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

          <ToolBtn
            active={toolMode === 'text'}
            onClick={() => setToolMode('text')}
            title="Add text label"
          >
            <NavIcon name="edit" /> Text
          </ToolBtn>
          <ToolBtn
            onClick={() =>
              setModal({ kind: 'new-field', bindingKey: 'patient_name', customLabel: '' })
            }
            title="Add field placeholder (binding)"
          >
            <NavIcon name="clipboard" /> Field
          </ToolBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

          <ToolBtn onClick={deleteSelected} title="Delete selected overlay">
            <NavIcon name="trash" /> Delete
          </ToolBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

          <ToolBtn onClick={() => fileInputRef.current?.click()} title="Replace template image">
            <NavIcon name="camera" /> {imageUrl ? 'Replace Image' : 'Upload Image'}
          </ToolBtn>

          {overlays.length > 0 && (
            <span
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginLeft: 4 }}
            >
              {overlays.length} overlay{overlays.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Canvas area: image + overlays */}
      <div style={{ padding: '0 var(--sp-6)', paddingBottom: 'var(--sp-6)' }}>
        <div ref={canvasContainerRef}>
          {!imageUrl ? (
            <div
              style={{
                width: '100%',
                aspectRatio: '4 / 3',
                borderRadius: 'var(--radius-md)',
                border: '2px dashed var(--border)',
                background: 'var(--bg-surface-2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <NavIcon name="camera" />
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                {uploading ? 'Uploading...' : 'Click to upload a template image'}
              </span>
            </div>
          ) : (
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: '#fff',
              }}
            >
              <Stage
                ref={stageRef}
                width={canvasWidth}
                height={canvasHeight}
                onClick={handleStageClick}
                onTap={
                  handleStageClick as unknown as (evt: Konva.KonvaEventObject<TouchEvent>) => void
                }
              >
                {/* Base image layer */}
                <Layer>
                  <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill="#ffffff" />
                  {loadedImage && (
                    <KonvaImage
                      image={loadedImage}
                      x={0}
                      y={0}
                      width={canvasWidth}
                      height={canvasHeight}
                      listening={false}
                    />
                  )}
                </Layer>

                {/* Overlays layer */}
                <Layer>
                  {overlays.map((o) => {
                    const isSelected = selectedId === o.id;
                    switch (o.type) {
                      case 'text':
                        return (
                          <Text
                            key={o.id}
                            id={o.id}
                            x={o.x}
                            y={o.y}
                            text={o.text}
                            fontSize={o.fontSize}
                            fill={isSelected ? '#2563eb' : '#1a1a1a'}
                            fontStyle="bold"
                            draggable
                            onClick={() => setSelectedId(o.id)}
                            onTap={() => setSelectedId(o.id)}
                            onDragEnd={(e) =>
                              updateOverlay(o.id, { x: e.target.x(), y: e.target.y() })
                            }
                            onDblClick={() =>
                              setModal({ kind: 'edit-text', overlayId: o.id, value: o.text })
                            }
                          />
                        );
                      case 'field':
                        return (
                          <FieldPlaceholder
                            key={o.id}
                            overlay={o}
                            isSelected={isSelected}
                            onSelect={() => setSelectedId(o.id)}
                            onUpdate={(changes) => updateOverlay(o.id, changes)}
                            onEditBinding={() =>
                              setModal({
                                kind: 'edit-field',
                                overlayId: o.id,
                                bindingKey: o.bindingKey,
                                customLabel: o.bindingKey === 'custom' ? o.label : '',
                              })
                            }
                          />
                        );
                    }
                  })}
                  <Transformer
                    ref={transformerRef}
                    rotateEnabled={false}
                    borderStroke="#2563eb"
                    anchorStroke="#2563eb"
                    anchorFill="#fff"
                    anchorSize={8}
                  />
                </Layer>
              </Stage>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* ─── Edit modals ─── */}
      {modal?.kind === 'edit-text' && (
        <OverlayModal title="Edit Text" onClose={() => setModal(null)}>
          <input
            autoFocus
            value={modal.value}
            onChange={(e) => setModal({ ...modal, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateOverlay(modal.overlayId, { text: modal.value });
                setModal(null);
              }
            }}
            placeholder="Label text..."
            style={modalInputStyle}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setModal(null)} style={mutedBtnStyle}>
              Cancel
            </button>
            <button
              onClick={() => {
                updateOverlay(modal.overlayId, { text: modal.value });
                setModal(null);
              }}
              style={primaryBtnStyle}
            >
              Apply
            </button>
          </div>
        </OverlayModal>
      )}

      {(modal?.kind === 'edit-field' || modal?.kind === 'new-field') && (
        <OverlayModal
          title={modal.kind === 'new-field' ? 'Add Field' : 'Edit Field Binding'}
          onClose={() => setModal(null)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {FIELD_BINDING_OPTIONS.map((opt) => (
              <label
                key={opt.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border:
                    modal.bindingKey === opt.key
                      ? '1.5px solid var(--color-primary)'
                      : '1px solid var(--border)',
                  background:
                    modal.bindingKey === opt.key ? 'rgba(59,130,246,0.06)' : 'transparent',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <input
                  type="radio"
                  name="binding"
                  checked={modal.bindingKey === opt.key}
                  onChange={() =>
                    setModal({
                      ...modal,
                      bindingKey: opt.key,
                      customLabel: opt.key === 'custom' ? modal.customLabel : '',
                    })
                  }
                  style={{ accentColor: 'var(--color-primary)' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {modal.bindingKey === 'custom' && (
            <input
              autoFocus
              value={modal.customLabel}
              onChange={(e) => setModal({ ...modal, customLabel: e.target.value })}
              placeholder="Custom label..."
              style={{ ...modalInputStyle, marginTop: 10 }}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setModal(null)} style={mutedBtnStyle}>
              Cancel
            </button>
            <button
              onClick={() => {
                const picked = FIELD_BINDING_OPTIONS.find((o) => o.key === modal.bindingKey);
                const label =
                  modal.bindingKey === 'custom'
                    ? modal.customLabel.trim() || 'Custom'
                    : (picked?.label ?? 'Field');
                if (modal.kind === 'new-field') {
                  // Create new field overlay with chosen binding
                  const id = crypto.randomUUID().slice(0, 8);
                  const cx = canvasWidth / 2;
                  const cy = canvasHeight / 2;
                  const item: FieldOverlay = {
                    id,
                    type: 'field',
                    x: cx - 70,
                    y: cy - 14,
                    bindingKey: modal.bindingKey,
                    label,
                    width: Math.max(label.length * 9 + 20, 100),
                    height: 28,
                  };
                  setOverlays((prev) => [...prev, item]);
                  setSelectedId(id);
                } else {
                  updateOverlay(modal.overlayId, { bindingKey: modal.bindingKey, label });
                }
                setModal(null);
              }}
              style={primaryBtnStyle}
            >
              {modal.kind === 'new-field' ? 'Add' : 'Apply'}
            </button>
          </div>
        </OverlayModal>
      )}
    </div>
  );
}

// ─── Overlay edit modal shell ───

function OverlayModal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 998 }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 360,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 999,
          padding: 'var(--sp-5)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

const modalInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  outline: 'none',
};

const primaryBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-primary)',
  background: 'var(--color-primary)',
  color: '#fff',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  cursor: 'pointer',
};

// ─── Field placeholder (rect + label) ───

function FieldPlaceholder({
  overlay,
  isSelected,
  onSelect,
  onUpdate,
  onEditBinding,
}: {
  overlay: FieldOverlay;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (changes: Partial<FieldOverlay>) => void;
  onEditBinding: () => void;
}) {
  return (
    <>
      <Rect
        id={overlay.id}
        x={overlay.x}
        y={overlay.y}
        width={overlay.width}
        height={overlay.height}
        fill={isSelected ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.05)'}
        stroke={isSelected ? '#2563eb' : '#93a3b8'}
        strokeWidth={1}
        dash={[4, 2]}
        cornerRadius={3}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onUpdate({ x: e.target.x(), y: e.target.y() })}
        onDblClick={onEditBinding}
      />
      <Text
        x={overlay.x + 6}
        y={overlay.y + 7}
        text={`{${overlay.label}}`}
        fontSize={11}
        fill={isSelected ? '#2563eb' : '#64748b'}
        fontStyle="italic"
        listening={false}
      />
    </>
  );
}

// ─── Shared button styles ───

const dangerBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--color-danger-text, #e53e3e)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
};

const mutedBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
};
